import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { DropdownList, IconInfo, Stack } from '@adc/parallax-component-library';
import { faker } from '@faker-js/faker';
import { useForm, useStore } from '@tanstack/react-form';
import * as z from 'zod';

import { getFakeIDNData } from '~/api/msw/api/data';
import { DropdownInput } from '~/components/DropdownInput';
import { CLMTextInput } from '~/components/inputs/CLMTextInput';
import { CountryListItems, CountryOptions } from '~/constants/forms';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import { departmentSchema } from './validations';

export type DepartmentFormValues = z.infer<typeof departmentSchema>;

export type DepartmentFormRef = {
  submitForm: () => Promise<void>;
};

type DepartmentFormProps = {
  testID: string;
  onSubmit: (values: DepartmentFormValues) => Promise<void>;
  defaultValues?: Partial<DepartmentFormValues>;
  hiddenFields?: (keyof DepartmentFormValues)[];
  disabledFields?: (keyof DepartmentFormValues)[];
};

const getBaseDefaultValues = (): DepartmentFormValues => {
  // FIXME: replace with real data when API integration will be done
  const fakeIDNData = faker.helpers.arrayElement(getFakeIDNData());

  return {
    id: faker.string.numeric(6),
    careTeamId: faker.string.numeric(6),
    idnName: fakeIDNData.name,
    idnId: fakeIDNData.id,
    name: '',
    address: '',
    addressLine2: undefined,
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    phone: undefined,
  };
};

export const DepartmentForm = forwardRef<
  DepartmentFormRef,
  DepartmentFormProps
>(({ testID, onSubmit, defaultValues, hiddenFields, disabledFields }, ref) => {
  const { openModal } = useModal();
  const [baseDefaultValues] = useState(getBaseDefaultValues);

  const form = useForm({
    defaultValues: {
      ...baseDefaultValues,
      ...defaultValues,
    },
    validators: { onChange: departmentSchema },
    onSubmit: ({ value }) => {
      return onSubmit(departmentSchema.parse(value));
    },
  });

  const countryValue = useStore(form.store, (s) => s.values.country);

  const { countrySelectedIndex, selectedCountryLabel } = useMemo(() => {
    const index = Object.keys(CountryOptions).indexOf(
      countryValue as keyof typeof CountryOptions,
    );
    const label = CountryOptions[countryValue as keyof typeof CountryOptions];

    return {
      countrySelectedIndex: index,
      selectedCountryLabel: label,
    };
  }, [countryValue]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: form.handleSubmit,
    }),
    [form.handleSubmit],
  );

  const openCareTeamIdInformationModal = useCallback(() => {
    openModal('CareTeamIdInformation', {
      testID: 'care-team-id-information-modal',
    });
  }, [openModal]);

  return (
    <Stack gap="$6" paddingTop="$0" testID={testID}>
      <Stack flexDirection="row" gap="$6">
        {!hiddenFields?.includes('idnName') && (
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="idnName">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'name'],
                  })}
                  label="IDN Name"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                  disabled
                />
              )}
            </form.Field>
          </Stack>
        )}
        {!hiddenFields?.includes('idnId') && (
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="idnId">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'id'],
                  })}
                  label="IDN ID"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  {...getErrorProps(field)}
                  disabled
                />
              )}
            </form.Field>
          </Stack>
        )}
      </Stack>
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} width="50%">
          <form.Field name="name">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'name'],
                })}
                label="Department Name"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
                disabled={disabledFields?.includes('name')}
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} width="50%">
          {!hiddenFields?.includes('careTeamId') && (
            <form.Field name="careTeamId">
              {(field) => (
                <CLMTextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'care', 'team', 'id'],
                  })}
                  aria-label="Care team ID"
                  label="Care team ID"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  inlineIconConfig={{
                    icon: <IconInfo />,
                    'aria-label': 'Care team ID informations',
                    onPress: openCareTeamIdInformationModal,
                    color: disabledFields?.includes('careTeamId')
                      ? '$interactive.primary.disabled.onColor'
                      : '$interactive.core.enabled',
                  }}
                  {...getErrorProps(field)}
                  disabled={disabledFields?.includes('careTeamId')}
                />
              )}
            </form.Field>
          )}
        </Stack>
      </Stack>
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} width="50%">
          <form.Field name="address">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'address'],
                })}
                label="Department Address"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
                disabled={disabledFields?.includes('address')}
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} width="50%">
          <form.Field name="addressLine2">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'address', 'line2'],
                })}
                label="Department Address Line 2 (Optional)"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
                disabled={disabledFields?.includes('addressLine2')}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1}>
          <form.Field name="city">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'city'],
                })}
                label="City"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
                disabled={disabledFields?.includes('city')}
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1}>
          <form.Field name="state">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'state'],
                })}
                label="State/Province"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
                disabled={disabledFields?.includes('state')}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} width="50%">
          <form.Field name="zipCode">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'zip', 'code'],
                })}
                label="ZIP Code"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
                disabled={disabledFields?.includes('zipCode')}
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} width="50%">
          <form.Field name="country">
            {(field) => (
              <DropdownList
                testID={buildTestId({
                  testID,
                  suffix: ['dropdown', 'country'],
                })}
                items={CountryListItems}
                selectedIndex={countrySelectedIndex}
                onSelect={(index) => {
                  const selectedCountryCode = Object.keys(CountryOptions)[
                    index
                  ] as keyof typeof CountryOptions;

                  field.setValue(selectedCountryCode);
                }}
              >
                <DropdownInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'country'],
                  })}
                  aria-label="Country"
                  label="Country"
                  value={selectedCountryLabel}
                  {...getErrorProps(field)}
                  disabled={disabledFields?.includes('country')}
                />
              </DropdownList>
            )}
          </form.Field>
        </Stack>
      </Stack>
    </Stack>
  );
});

DepartmentForm.displayName = 'DepartmentForm';
